/**
 * Companion-product add-on toggle (any product page).
 *
 * Direction-agnostic: the block offers whatever product the merchant picked in the
 * theme editor, alongside whatever product the page itself sells. The toilet-paper
 * page offers the kitchen paper; the kitchen-paper page offers the toilet paper.
 * Nothing here knows which is which — "kp" in the selectors and variable names is
 * historical (kjøkkenpapir was the first companion) and now just means "the
 * companion product this block offers".
 *
 * Reads the companion product's selling plans at runtime via fetch(/products/<handle>.js)
 * and matches by delivery interval (weeks) with whichever plan of the page's own product
 * is currently selected in the buy box. Never hardcodes a selling_plan id — both sides are resolved
 * live so this keeps working if plans are recreated (e.g. by the Kaching app) after
 * this theme file was pushed.
 *
 * The block stays hidden until: (1) a TP subscription plan is actively selected with a
 * known interval (4 or 8 weeks — the only two the copy has labels for), and (2) the
 * add-on product exposes a selling plan with a matching interval. This avoids showing a
 * control that cannot be fulfilled, or a label with the wrong price.
 *
 * Cart state is treated as the source of truth, not a page-session variable:
 *  - On load, /cart.js is read once to adopt any existing kitchen-paper line (its key
 *    and selling_plan), so a reload with the item already in cart renders checked and
 *    unchecking removes the real line instead of merging a duplicate add.
 *  - If the customer changes the TP interval while the add-on is checked, the existing
 *    line is swapped (remove + add) to the newly matched plan rather than left stale, or
 *    removed outright if the new interval has no matching add-on plan.
 */
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function extractWeeks(text) {
    if (!text) return null;
    var match = String(text).match(/(\d+)\s*\.?\s*uke/i);
    return match ? parseInt(match[1], 10) : null;
  }

  // Kaching (and most Shopify selling-plan apps) encode the real delivery
  // interval as a JSON blob in an option value, e.g.
  // {"delivery":{"interval":"WEEK","frequency":4}, ...}. That is the
  // authoritative source: read it first, and only fall back to scanning the
  // human-readable plan name (which is marketing copy and can be reworded)
  // when no structured interval is present.
  function structuredWeeks(plan) {
    if (!plan || !Array.isArray(plan.options)) return null;
    for (var i = 0; i < plan.options.length; i++) {
      var opt = plan.options[i];
      if (!opt || typeof opt.value !== 'string') continue;
      var parsed;
      try {
        parsed = JSON.parse(opt.value);
      } catch (e) {
        continue;
      }
      var delivery = parsed && parsed.delivery;
      if (!delivery || typeof delivery.frequency !== 'number' || !delivery.interval) continue;
      switch (String(delivery.interval).toUpperCase()) {
        case 'WEEK':
          return delivery.frequency;
        case 'DAY':
          return delivery.frequency / 7;
        case 'MONTH':
          return delivery.frequency * 4.345;
        default:
          continue;
      }
    }
    return null;
  }

  function planWeeks(plan) {
    if (!plan) return null;

    var structured = structuredWeeks(plan);
    if (structured !== null) return structured;

    var pieces = [plan.name || '', plan.description || ''];
    if (Array.isArray(plan.options)) {
      plan.options.forEach(function (opt) {
        if (opt && opt.name) pieces.push(opt.name);
      });
    }
    return extractWeeks(pieces.join(' '));
  }

  function flattenPlans(groups) {
    var plans = [];
    (groups || []).forEach(function (group) {
      (group.selling_plans || []).forEach(function (plan) {
        plans.push(plan);
      });
    });
    return plans;
  }

  function readJsonScript(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // The only intervals the add-on copy has labels for. Published below so the
  // bundle popup gates on exactly the same set: if the popup offered an
  // interval this block does not support, this block's reconciliation would
  // turn around and remove the line the customer just accepted.
  var SUPPORTED_WEEKS = [4, 8];

  function isSupportedWeeks(weeks) {
    return SUPPORTED_WEEKS.indexOf(weeks) !== -1;
  }

  // Published so sibling features (the bundle upsell popup) reuse this exact
  // matching logic instead of carrying a second copy that can drift from it.
  // Defined before the init guard below, so the helpers exist even on a
  // second evaluation of this file.
  window.MjuktKpPlans = window.MjuktKpPlans || {
    extractWeeks: extractWeeks,
    structuredWeeks: structuredWeeks,
    planWeeks: planWeeks,
    flattenPlans: flattenPlans,
    readJsonScript: readJsonScript,
    supportedWeeks: SUPPORTED_WEEKS.slice(),
    isSupportedWeeks: isSupportedWeeks
  };

  // Both the add-on block and the popup block request this asset; Liquid
  // renders the tag once, but guard against a double evaluation binding every
  // listener twice.
  if (window.__mjuktKpAddonInit) return;
  window.__mjuktKpAddonInit = true;

  ready(function () {
    var addons = document.querySelectorAll('[data-kp-addon]');
    if (!addons.length || !window.Shopify || !window.Shopify.routes) return;

    var tpForm = document.getElementById('mjukt-product-form');
    var tpPlans = flattenPlans(readJsonScript(document.querySelector('[data-tp-selling-plans]')));

    addons.forEach(initAddon);

    function initAddon(addon) {
      var checkbox = addon.querySelector('[data-kp-addon-checkbox]');
      var labelEl = addon.querySelector('[data-kp-addon-label]');
      var benefitEl = addon.querySelector('[data-kp-addon-benefit]');
      var statusEl = addon.querySelector('[data-kp-addon-status]');
      if (!checkbox || !labelEl) return;

      // Keys are looked up per interval (`checkbox_label_4w`, `benefit_8w`, …),
      // so a new supported interval only needs new settings, not new code.
      var copyScript = addon.nextElementSibling;
      var copy = (copyScript && copyScript.matches('[data-kp-addon-copy]') && readJsonScript(copyScript)) || {};

      var kpHandle = addon.getAttribute('data-kp-product-handle');
      var kpVariantId = addon.getAttribute('data-kp-variant-id');
      var kpPlans = null;
      var kpFetchPromise = null;

      // Cart truth, not page-session state: lineKey/lineSellingPlanId always
      // reflect what's actually in the cart (adopted on load, then kept in
      // sync after every add/remove/swap this page performs).
      var lineKey = null;
      var lineSellingPlanId = null;
      var matchedPlanId = null;
      var busy = false;
      var refreshQueued = false;

      function setStatus(msg) {
        if (!statusEl) return;
        statusEl.textContent = msg || '';
        statusEl.hidden = !msg;
      }

      function adoptCartLine(cart) {
        var line = null;
        if (cart && Array.isArray(cart.items)) {
          for (var i = 0; i < cart.items.length; i++) {
            var item = cart.items[i];
            if (String(item.id) === String(kpVariantId)) {
              line = item;
              break;
            }
          }
        }
        if (line) {
          lineKey = line.key;
          lineSellingPlanId =
            line.selling_plan_allocation && line.selling_plan_allocation.selling_plan
              ? line.selling_plan_allocation.selling_plan.id
              : null;
        } else {
          lineKey = null;
          lineSellingPlanId = null;
        }
      }

      // Re-readable rather than a one-shot promise: another feature on this
      // page (the bundle popup) can add or remove the kitchen-paper line after
      // load, and announces it with `mjukt:kp-cart-changed` so this block
      // re-adopts the real cart line instead of rendering stale state.
      var cartStateReady;

      function loadCartState() {
        cartStateReady = fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .then(function (cart) {
            adoptCartLine(cart);
          })
          .catch(function () {
            // No cart state available; fall back to assuming the line isn't there.
            lineKey = null;
            lineSellingPlanId = null;
          });
        return cartStateReady;
      }

      loadCartState();

      function fetchKpPlans() {
        if (kpFetchPromise) return kpFetchPromise;
        kpFetchPromise = fetch(window.Shopify.routes.root + 'products/' + kpHandle + '.js', {
          headers: { Accept: 'application/json' }
        })
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .then(function (data) {
            if (data) {
              kpVariantId = (data.variants && data.variants[0] && data.variants[0].id) || kpVariantId;
              kpPlans = flattenPlans(data.selling_plan_groups);
            } else {
              kpPlans = [];
            }
            return kpPlans;
          })
          .catch(function () {
            kpPlans = [];
            return kpPlans;
          });
        return kpFetchPromise;
      }

      function getSelectedTpPlanId() {
        if (tpForm) {
          var field = tpForm.querySelector('[name="selling_plan"]');
          if (field && field.value) return field.value;
        }
        var fallback = document.querySelector('[name="selling_plan"]');
        return fallback && fallback.value ? fallback.value : null;
      }

      function findTpPlanById(id) {
        var matches = tpPlans.filter(function (p) {
          return String(p.id) === String(id);
        });
        return matches[0] || null;
      }

      // Only 4- and 8-week copy exists in the block's schema. Any other
      // interval (unmatched TP plan, or a future plan the copy doesn't cover
      // yet) hides the block instead of showing a price that doesn't apply.
      function computeWeeks() {
        var selectedId = getSelectedTpPlanId();
        var tpPlan = selectedId ? findTpPlanById(selectedId) : null;
        var weeks = tpPlan ? planWeeks(tpPlan) : null;
        return isSupportedWeeks(weeks) ? weeks : null;
      }

      // Both the checkbox label and the price line are per interval, because the
      // companion's share of the bundle price depends on the delivery interval in
      // one direction (toilet paper added to a kitchen-paper subscription costs
      // less on the 4-week plan than on the 8-week one) even though it happens
      // not to in the other. A blank per-interval string leaves the server-rendered text alone,
      // so a block that only needs one fixed line still works with these empty.
      function applyCopy(weeks) {
        var label = copy['checkbox_label_' + weeks + 'w'];
        if (label) labelEl.textContent = label;
        var benefit = copy['benefit_' + weeks + 'w'];
        if (benefitEl && benefit) benefitEl.textContent = benefit;
      }

      function syncUI(weeks) {
        // Copy first, then reveal: the block must never be visible for even one
        // frame carrying the price line of a different interval.
        if (matchedPlanId) applyCopy(weeks);
        addon.hidden = !matchedPlanId;
        checkbox.checked = !!lineKey;
        if (refreshQueued) {
          refreshQueued = false;
          refresh();
        }
      }

      // Reconciles the visible interval selection against the cart:
      //  - no matching plan for the current interval -> remove the line if present
      //  - matching plan, but the existing line is on a different plan -> swap it
      //  - matching plan, line already correct (or absent) -> just sync the UI
      function refresh() {
        if (busy) {
          refreshQueued = true;
          return;
        }
        cartStateReady.then(function () {
          if (busy) {
            refreshQueued = true;
            return;
          }
          var weeks = computeWeeks();
          fetchKpPlans().then(function (plans) {
            if (busy) {
              refreshQueued = true;
              return;
            }
            var kpMatches = weeks
              ? (plans || []).filter(function (p) {
                  return planWeeks(p) === weeks;
                })
              : [];
            var kpPlan = kpMatches[0] || null;
            matchedPlanId = kpPlan ? kpPlan.id : null;

            if (!matchedPlanId) {
              if (lineKey) {
                removeLine().then(function () {
                  syncUI(weeks);
                });
              } else {
                syncUI(weeks);
              }
              return;
            }

            if (lineKey && String(lineSellingPlanId) !== String(matchedPlanId)) {
              swapLine(matchedPlanId).then(function () {
                syncUI(weeks);
              });
              return;
            }

            syncUI(weeks);
          });
        });
      }

      function syncCartDrawerFrom(data) {
        var cartDrawerEl = document.querySelector('cart-drawer');
        if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function' && data && data.sections) {
          cartDrawerEl.renderContents(data);
        }
      }

      function getSectionsToRender() {
        var cartDrawerEl = document.querySelector('cart-drawer');
        if (cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === 'function') {
          return cartDrawerEl.getSectionsToRender().map(function (s) {
            return s.id;
          });
        }
        return [];
      }

      // Low-level cart primitives. Each owns the busy/disabled lifecycle for
      // its own network call and updates lineKey/lineSellingPlanId on success;
      // callers (refresh's reconciliation, or the checkbox handler) decide
      // what the UI should look like afterwards.
      function addLine(planId) {
        busy = true;
        checkbox.disabled = true;
        setStatus('Legger til …');

        return fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: kpVariantId, quantity: 1, selling_plan: planId }],
            sections: getSectionsToRender(),
            sections_url: window.location.pathname
          })
        })
          .then(function (res) {
            return res.json();
          })
          .then(function (data) {
            if (data.status) {
              setStatus('Kunne ikke legges til. Prøv igjen.');
              return data;
            }
            lineKey = data.key;
            lineSellingPlanId = planId;
            setStatus('');
            syncCartDrawerFrom(data);
            return data;
          })
          .catch(function () {
            setStatus('Kunne ikke legges til. Prøv igjen.');
          })
          .finally(function () {
            busy = false;
            checkbox.disabled = false;
          });
      }

      // Resolves `true` only when the line is confirmed gone (or there was
      // never one to remove), `false` on any network/response failure. This
      // return value is load-bearing for swapLine(): a `.catch()` that just
      // swallows the error would leave the chained promise fulfilled, and a
      // caller doing `removeLine().then(addLine)` would add the new line
      // regardless of whether the old one actually got removed.
      function removeLine() {
        if (!lineKey) return Promise.resolve(true);
        busy = true;
        checkbox.disabled = true;
        var keyToRemove = lineKey;

        return fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: keyToRemove,
            quantity: 0,
            sections: getSectionsToRender(),
            sections_url: window.location.pathname
          })
        })
          .then(function (res) {
            return res.json();
          })
          .then(function (data) {
            lineKey = null;
            lineSellingPlanId = null;
            setStatus('');
            syncCartDrawerFrom(data);
            return true;
          })
          .catch(function () {
            // Leave lineKey/lineSellingPlanId untouched — the line is still
            // (as far as we know) sitting in the cart on its old plan.
            setStatus('Kunne ikke fjerne. Prøv igjen.');
            return false;
          })
          .finally(function () {
            busy = false;
            checkbox.disabled = false;
          });
      }

      // Remove-then-add. If the remove leg didn't actually succeed, abort —
      // do NOT add a second line on top of one that may still be there. The
      // caller's post-swap UI sync (refresh()'s syncUI) then reads the
      // still-intact lineKey/lineSellingPlanId and reflects the real,
      // unswapped cart state instead of a phantom new line.
      function swapLine(newPlanId) {
        return removeLine().then(function (removed) {
          if (!removed) return false;
          return addLine(newPlanId);
        });
      }

      checkbox.addEventListener('change', function () {
        if (checkbox.checked) {
          if (!matchedPlanId) {
            checkbox.checked = false;
            return;
          }
          addLine(matchedPlanId).then(function () {
            checkbox.checked = !!lineKey;
          });
        } else {
          removeLine().then(function () {
            checkbox.checked = !!lineKey;
          });
        }
      });

      refresh();

      // Fired by the bundle popup after it adds the kitchen paper, so the
      // checkbox adopts that line instead of offering to add a duplicate.
      document.addEventListener('mjukt:kp-cart-changed', function () {
        loadCartState().then(refresh);
      });

      if (tpForm) tpForm.addEventListener('change', refresh);
      document.addEventListener('change', function (e) {
        if (e.target && e.target.name === 'selling_plan') refresh();
      });

      var watchTarget = document.querySelector('.subscriptions_app_embed_block') || tpForm;
      if (watchTarget) {
        var mo = new MutationObserver(refresh);
        mo.observe(watchTarget, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
      }

      var toggleWrap = document.querySelector('[data-purchase-toggle]');
      if (toggleWrap) {
        var toggleBtns = toggleWrap.querySelectorAll('[data-toggle-option]');
        toggleBtns.forEach(function (btn) {
          btn.addEventListener('click', function () {
            window.setTimeout(refresh, 300);
          });
        });
      }
    }
  });
})();
